import type { RootState } from '@/app/store';
import { db } from '@/lib/db';
import { upsertSearchHistory } from '@/lib/account-data';
import { prepareSearchClause, quoteSqlString } from '@/lib/search-query';
import type { CountyShape, ParcelDetails, SearchFipsParams, SearchParams, SearchResult } from "@/types";
import type Graphic from "@arcgis/core/Graphic";
import type Point from "@arcgis/core/geometry/Point";
import Polygon from "@arcgis/core/geometry/Polygon";
import esriRequest from "@arcgis/core/request";
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected, PayloadAction } from "@reduxjs/toolkit";

export interface SearchState {
    searching: boolean,
    counties: CountyShape[],
    searchResults: SearchResult[],
    selectedSearchResult: SearchResult | null
    selectParcelShape: object | null
    parcelShapeFipsAPN: string | null
    parcelShapeError: string | null
    resultsRequestId: string | null
    selectionRequestId: string | null
    error: string | null
}

export const loadCountiesShapes = createAsyncThunk<CountyShape[], void>(
    'search/loadCountiesShapes',
    async () => {
        if (await db.countyShapes.count() > 0) {
            return await db.countyShapes.toArray();
        }
        const response = await esriRequest(`${import.meta.env.VITE_ESRI_COUNTY_LAYER_URL}/query`, {
            responseType: 'json',
            query: {
                f: 'json',
                where: '1=1',
                outfields: '*',
                returnGeometry: true,
                token: import.meta.env.VITE_ESRI_API_KEY as string
            },
        })
        const counties = response.data.features.map((feature: { geometry: object; attributes: { NAMELSAD: string } }) => {
            const extent = new Polygon(feature.geometry).extent?.toJSON();
            return { name: feature.attributes.NAMELSAD, shape: { ...extent, spatialReference: { wkid: 3857 } } };
        });
        await db.countyShapes.bulkAdd(counties);
        return counties;
    }
);

export const getSearchResults = createAsyncThunk<SearchResult[], SearchParams>(
    'search/getSearchResults',
    async (params, { signal }) => {
        const searchParams = { ...params, county: params.county.trim() };
        const pointLayerResults = await getPointLayerSearchResults(searchParams, signal);
        const results = pointLayerResults.length > 0 ? pointLayerResults : await getGeocodeSuggestSearchResults(searchParams, signal);
        return results.sort(sortSearchResults);
    }
)

export const getPointLayerSearchResultByFipsApn = createAsyncThunk<SearchResult | null, SearchFipsParams>(
    'search/getPointLayerSearchResultByFipsApn',
    async (searchParams, { signal }): Promise<SearchResult | null> => {
        const query = searchParams.pointLayer.createQuery();
        query.where = `fips_apn IN (${searchParams.fipsApn.map(quoteSqlString).join(',')})`;
        query.outFields = ['*'];
        query.returnGeometry = true;
        query.num = 1000;
        query.start = 0;
        const response = await searchParams.pointLayer.queryFeatures(query, { signal });
        // A click can hit several stacked unit points; prefer the lowest unit number.
        const unitFeatures = response.features.filter((f) => f.attributes.s_unit).sort(compareUnits);
        const feature = unitFeatures[0] ?? response.features[0];
        if (!feature) {
            return null;
        }
        const attributes = feature.attributes;
        return featureToSearchResult(feature, {
            text: '',
            result: `${attributes.apn_d}\r\n${attributes.s_addr_d}\r\n${attributes.m_city}`,
            county: attributes.county_name,
            type: 'apn',
            mode: 'apn',
        });
    }
)

export const selectSearchResult = createAsyncThunk(
    'search/selectSearchResult',
    async ({ searchResult, ownerId }: { searchResult: SearchResult; ownerId: string }, { signal }) => {
        const currentResult = {... searchResult};
        if (currentResult.type === 'geocode') {
            const response = await esriRequest(`${import.meta.env.VITE_ESRI_GEOCODE_URL}/findAddressCandidates`, {
                signal,
                responseType: 'json',
                query: {
                    f: 'json',
                    SingleLine: currentResult.address,
                    outFields: '*',
                    magicKey: currentResult.magicKey,
                    maxLocations: 1,
                    token: import.meta.env.VITE_ESRI_API_KEY as string
                },
            })
            currentResult.shape = response.data.candidates[0]?.location;
            if (!currentResult.shape) throw new Error('No location was found for this result.');
        }
        signal.throwIfAborted();
        await upsertSearchHistory(ownerId, { search: currentResult.text, county: currentResult.county, mode: currentResult.mode });
        return currentResult;
    }
)

export const initialState: SearchState = {
    searching: false,
    counties: [],
    searchResults: [],
    selectedSearchResult: null,
    selectParcelShape: null,
    parcelShapeFipsAPN: null,
    parcelShapeError: null,
    resultsRequestId: null,
    selectionRequestId: null,
    error: null,
}

export const searchSlice = createSlice({
    name: 'search',
    initialState,
    reducers: {
        setSearchResults: (state, action: PayloadAction<SearchResult[]>) => {
            state.searchResults = action.payload;
            state.resultsRequestId = null;
            state.searching = state.selectionRequestId !== null;
            state.error = null;
        },
        setSelectedSearchResult: (state, action: PayloadAction<SearchResult | null>) => {
            state.selectedSearchResult = action.payload;
            state.selectionRequestId = null;
            state.searching = state.resultsRequestId !== null;
            state.selectParcelShape = null;
            state.parcelShapeFipsAPN = null;
            state.parcelShapeError = null;
        },
        setSelectParcelShape: (state, action: PayloadAction<{ fipsAPN: string; shape: object }>) => {
            if (state.selectedSearchResult?.fipsAPN !== action.payload.fipsAPN) return;
            state.selectParcelShape = action.payload.shape;
            state.parcelShapeFipsAPN = action.payload.fipsAPN;
            state.parcelShapeError = null;
        },
        clearParcelShape: (state) => {
            state.selectParcelShape = null;
            state.parcelShapeFipsAPN = null;
            state.parcelShapeError = null;
        },
        setParcelShapeError: (state, action: PayloadAction<{ fipsAPN: string; message: string }>) => {
            if (state.selectedSearchResult?.fipsAPN !== action.payload.fipsAPN) return;
            state.selectParcelShape = null;
            state.parcelShapeFipsAPN = null;
            state.parcelShapeError = action.payload.message;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(loadCountiesShapes.fulfilled, (state, action) => {
            state.counties = action.payload;
        });
        builder.addCase(loadCountiesShapes.rejected, (state, action) => {
            console.log(action.error.message);
            state.counties = [];
        });
        builder.addCase(getSearchResults.pending, (state, action) => {
            state.searching = true;
            state.resultsRequestId = action.meta.requestId;
            state.error = null;
        });
        builder.addCase(getSearchResults.fulfilled, (state, action) => {
            if (state.resultsRequestId !== action.meta.requestId) return;
            state.searchResults = action.payload;
            state.resultsRequestId = null;
            state.searching = state.selectionRequestId !== null;
        });
        builder.addCase(getSearchResults.rejected, (state, action) => {
            if (state.resultsRequestId !== action.meta.requestId) return;
            state.searchResults = [];
            state.resultsRequestId = null;
            state.searching = state.selectionRequestId !== null;
            state.error = action.meta.aborted ? null : 'Search could not load. Check your connection and try again.';
        });
        // Choosing a listed result and tapping a parcel on the map share one selection slot.
        builder.addMatcher(isPending(selectSearchResult, getPointLayerSearchResultByFipsApn), (state, action) => {
            state.selectionRequestId = action.meta.requestId;
            state.searching = true;
            state.selectedSearchResult = null;
            state.selectParcelShape = null;
            state.parcelShapeFipsAPN = null;
            state.parcelShapeError = null;
            state.error = null;
        });
        builder.addMatcher(isFulfilled(selectSearchResult, getPointLayerSearchResultByFipsApn), (state, action) => {
            if (state.selectionRequestId !== action.meta.requestId) return;
            state.selectedSearchResult = action.payload;
            state.selectionRequestId = null;
            state.searching = state.resultsRequestId !== null;
        });
        builder.addMatcher(isRejected(selectSearchResult, getPointLayerSearchResultByFipsApn), (state, action) => {
            if (state.selectionRequestId !== action.meta.requestId) return;
            state.selectedSearchResult = null;
            state.selectionRequestId = null;
            state.searching = state.resultsRequestId !== null;
            const message = getPointLayerSearchResultByFipsApn.rejected.match(action) ? 'This parcel could not load. Please try again.' : 'This result could not load. Please try again.';
            state.error = action.meta.aborted ? null : message;
        });
    }
})

export const { setSearchResults, setSelectedSearchResult, setSelectParcelShape, clearParcelShape, setParcelShapeError } = searchSlice.actions;

export const currentSearchResults = (state: RootState) => state.search.searchResults;
export const isSearching = (state: RootState) => state.search.searching;
export const currentSelectedSearchResult = (state: RootState) => state.search.selectedSearchResult;
export const currentSelectedParcelShape = (state: RootState) => state.search.parcelShapeFipsAPN === state.search.selectedSearchResult?.fipsAPN ? state.search.selectParcelShape : null;
export const selectedParcelShapeError = (state: RootState) => state.search.parcelShapeError;
export const searchError = (state: RootState) => state.search.error;

export default searchSlice.reducer;

const sortSearchResults = (a: SearchResult, b: SearchResult) => {
    return a.result.localeCompare(b.result, 'en', { sensitivity: 'base' });
}

const getPointLayerSearchResults = async (searchParams: SearchParams, signal: AbortSignal): Promise<SearchResult[]> => {
    const search = searchParams.search?.trim()?.toUpperCase() ?? '';
    const query = searchParams.pointLayer.createQuery();
    query.where = prepareSearchClause(searchParams);
    query.outFields = ['*'];
    query.returnGeometry = true;
    query.num = 10;
    query.start = 0;
    const response = await searchParams.pointLayer.queryFeatures(query, { signal });

    return response.features.map((feature) => {
        const attributes = feature.attributes;
        const result = {
            apn: `${attributes.apn_d}\r\n${attributes.s_addr_d ?? ''}\r\n${attributes.s_city}`,
            owner: `${attributes.names}\r\n${attributes.s_addr_d ?? ''}\r\n${attributes.s_city ?? ''}`,
            address: `${attributes.s_addr_d}\r\n${attributes.s_city}`,
        }[searchParams.mode];
        return featureToSearchResult(feature, { text: search, result, county: searchParams.county, type: searchParams.mode, mode: searchParams.mode });
    });
}

const featureToSearchResult = (feature: Graphic, fields: Pick<SearchResult, 'text' | 'result' | 'county' | 'type' | 'mode'>): SearchResult => {
    const attributes = feature.attributes as ParcelDetails;
    return {
        address: attributes.s_addr_d ?? '',
        city: attributes.m_city,
        owner: attributes.names ?? '',
        fipsAPN: attributes.fips_apn,
        apn: attributes.apn_d,
        shape: (feature.geometry ?? undefined) as Point | undefined,
        magicKey: undefined,
        attributes,
        ...fields,
    };
}

/** Orders unit numbers numerically ("#2" before "#10"), with non-numeric units last. */
const compareUnits = (a: Graphic, b: Graphic) => {
    const aUnit = a.attributes.s_unit?.toUpperCase().replace('#', '') || '';
    const bUnit = b.attributes.s_unit?.toUpperCase().replace('#', '') || '';
    const aUnitNumber = parseInt(aUnit.replace(/[^0-9]/g, ''), 10);
    const bUnitNumber = parseInt(bUnit.replace(/[^0-9]/g, ''), 10);
    if (isNaN(aUnitNumber) && isNaN(bUnitNumber)) return aUnit.localeCompare(bUnit);
    if (isNaN(aUnitNumber)) return 1;
    if (isNaN(bUnitNumber)) return -1;
    return aUnitNumber !== bUnitNumber ? aUnitNumber - bUnitNumber : aUnit.localeCompare(bUnit);
}

const getGeocodeSuggestSearchResults = async (searchData: SearchParams, signal: AbortSignal): Promise<SearchResult[]> => {
    if (searchData.mode === 'apn') {
        return [];
    }
    const search = searchData.search?.trim() ?? '';
    const response = await esriRequest(`${import.meta.env.VITE_ESRI_GEOCODE_URL}/suggest`, {
        signal,
        responseType: 'json',
        query: {
            f: 'json',
            text: search,
            countryCode: 'USA',
            searchExtent: searchData.county ? JSON.stringify((await db.countyShapes.get({ name: `${searchData.county} County` }))?.shape) : undefined,
            maxSuggestions: 10,
            token: import.meta.env.VITE_ESRI_API_KEY as string
        },
    })
    return response.data?.suggestions?.map((suggestion: { text: string; magicKey: string }) => ({ text: search, result: suggestion.text, address: suggestion.text, owner: suggestion.text, county: searchData.county, type: 'geocode', magicKey: suggestion.magicKey, mode: searchData.mode } as SearchResult)) ?? [];
}
